import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories, categoryLabels } from '@/mocks/comics';

export default function Category() {
  const { type = 'all' } = useParams<{ type: string }>();
  const [activeType, setActiveType] = useState(type);

  useEffect(() => {
    setActiveType(type);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [type]);

  const filteredComics = activeType === 'all'
    ? comics
    : comics.filter(c => c.category === activeType);

  const currentCategory = categories.find(c => c.key === activeType);
  const pageTitle = categoryLabels[activeType] || '全部漫画';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: pageTitle },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://pipicomics.com',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': pageTitle,
        'item': `https://pipicomics.com/category/${activeType}`,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${pageTitle}`,
    'description': `皮皮漫画${currentCategory?.label || ''}专区，提供丰富的${currentCategory?.label || '漫画'}在线免费阅读。皮皮漫画类型大全，海量作品每日更新。`,
    'url': `https://pipicomics.com/category/${activeType}`,
    'inLanguage': 'zh-CN',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <section className="py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {pageTitle}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              皮皮漫画{currentCategory?.label || '漫画'}专区，海量{currentCategory?.label || '漫画'}免费在线阅读，每日更新热门作品
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-8 bg-background-100 rounded-full p-1 w-fit">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    activeType === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.label}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-5" data-product-shop>
                {filteredComics.map(comic => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该类型的漫画，请稍后再来</p>
              </div>
            )}

            {filteredComics.length > 0 && (
              <div className="mt-10 text-center">
                <p className="text-xs text-foreground-500">
                  共 {filteredComics.length} 部{currentCategory?.label || '漫画'}作品 —— 皮皮漫画免费在线阅读
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}